import { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Clock, AlertCircle, MapPin, Home, PartyPopper, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { authFetch, API } from '../utils/api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

function TimeTracking({ user }) {
  const [currentEntry, setCurrentEntry] = useState(null);
  const [elapsedTime, setElapsedTime] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [showLocationDialog, setShowLocationDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState('');

  useEffect(() => {
    // Detect if device is mobile using multiple methods
    const checkMobile = () => {
      // Method 1: User-Agent check
      const userAgent = navigator.userAgent || '';
      const userAgentMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());

      // Method 2: Touch support check
      const hasTouchScreen = (
        'ontouchstart' in window ||
        navigator.maxTouchPoints > 0 ||
        (navigator.msMaxTouchPoints && navigator.msMaxTouchPoints > 0)
      );

      // Method 3: Screen width check (mobile devices typically < 768px)
      const isSmallScreen = window.innerWidth < 768;

      // Method 4: Check for mobile-specific features
      const hasOrientationAPI = 'orientation' in window || 'onorientationchange' in window;

      // Combine checks: if any strong mobile indicator is present
      const mobile = userAgentMobile || (hasTouchScreen && isSmallScreen) || (hasTouchScreen && hasOrientationAPI);

      setIsMobile(mobile);
    };

    checkMobile();
    fetchCurrentEntry();
  }, []);

  useEffect(() => {
    if (!currentEntry) return;

    const interval = setInterval(() => {
      const start = new Date(currentEntry.clock_in);
      const now = new Date();
      const diff = Math.floor((now - start) / 1000);
      const hours = Math.floor(diff / 3600);
      const minutes = Math.floor((diff % 3600) / 60);
      const seconds = diff % 60;
      setElapsedTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentEntry]);

  const fetchCurrentEntry = async () => {
    try {
      const response = await authFetch(`${API}/time/current`);
      const data = await response.json();
      setCurrentEntry(data);
    } catch (error) {
      console.error('Error fetching current entry:', error);
    }
  };

  const openLocationDialog = () => {
    setShowLocationDialog(true);
    setSelectedLocation('');
  };

  const handleClockIn = async (location) => {
    try {
      const response = await authFetch(`${API}/time/clock-in`, {
        method: 'POST',
        body: JSON.stringify({ location })
      });

      if (response.ok) {
        const entry = await response.json();
        setCurrentEntry(entry);
        setShowLocationDialog(false);
        const locationLabels = {
          'onsite': 'Onsite',
          'wfh': 'Work from Home',
          'event': 'Event',
          'field': 'Field'
        };
        const locationLabel = locationLabels[location] || location;
        toast.success(`Clocked in successfully (${locationLabel})!`);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock in');
      }
    } catch (error) {
      toast.error('Failed to clock in');
    }
  };

  const handleClockOut = async () => {
    try {
      const response = await authFetch(`${API}/time/clock-out`, {
        method: 'POST'
      });

      if (response.ok) {
        const entry = await response.json();
        toast.success(`Clocked out! Duration: ${Math.floor(entry.duration / 60)}h ${entry.duration % 60}m`);
        setCurrentEntry(null);
        setElapsedTime('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to clock out');
      }
    } catch (error) {
      toast.error('Failed to clock out');
    }
  };

  return (
    <Layout user={user}>
      <div data-testid="time-tracking-page">
        <h1 className="text-3xl font-heading font-bold text-foreground-DEFAULT mb-6">Time Tracking</h1>

        <div className="max-w-2xl mx-auto">
          <div className="bg-white border border-slate-100 shadow-sm rounded-2xl p-12">
            <div className="text-center">
              {isMobile ? (
                <div className="py-8">
                  <div className="bg-orange-100 rounded-full w-40 h-40 flex items-center justify-center mx-auto mb-6">
                    <AlertCircle className="w-20 h-20 text-orange-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-foreground-DEFAULT mb-4">Desktop Only</h2>
                  <p className="text-foreground-muted max-w-md mx-auto">
                    Time tracking is only available on desktop devices. Please use a computer to clock in and out.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mb-8">
                    <p className="text-sm font-medium text-foreground-muted mb-4">
                      {currentEntry ? 'Currently Working' : 'Ready to start?'}
                    </p>
                    {currentEntry && (
                      <div className="bg-slate-50 rounded-xl px-8 py-6 inline-block mb-6">
                        <p className="text-6xl font-heading font-bold text-foreground-DEFAULT tracking-tight">
                          {elapsedTime}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-center">
                    {currentEntry ? (
                      <button
                        data-testid="clock-out-button"
                        onClick={handleClockOut}
                        className="bg-destructive text-white rounded-full w-40 h-40 flex flex-col items-center justify-center text-2xl font-bold shadow-xl shadow-destructive/20 hover:scale-105 transition-transform"
                      >
                        <Clock className="w-10 h-10 mb-3" />
                        Clock Out
                      </button>
                    ) : (
                      <button
                        data-testid="clock-in-button"
                        onClick={openLocationDialog}
                        className="bg-accent text-white rounded-full w-40 h-40 flex flex-col items-center justify-center text-2xl font-bold shadow-xl shadow-accent/20 hover:scale-105 transition-transform"
                      >
                        <Clock className="w-10 h-10 mb-3" />
                        Clock In
                      </button>
                    )}
                  </div>

                  {currentEntry && (
                    <div className="mt-8 text-left bg-slate-50 rounded-xl p-6">
                      <p className="text-sm text-foreground-muted mb-2">Started at</p>
                      <p className="text-lg font-semibold text-foreground-DEFAULT">
                        {new Date(currentEntry.clock_in).toLocaleString('en-US', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Location Selection Dialog */}
        <Dialog open={showLocationDialog} onOpenChange={setShowLocationDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Work Location</DialogTitle>
              <DialogDescription>
                Where are you working today?
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <button
                onClick={() => setSelectedLocation('onsite')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'onsite'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <MapPin className={`w-8 h-8 ${selectedLocation === 'onsite' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Onsite</span>
              </button>
              <button
                onClick={() => setSelectedLocation('wfh')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'wfh'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <Home className={`w-8 h-8 ${selectedLocation === 'wfh' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Work from Home</span>
              </button>
              <button
                onClick={() => setSelectedLocation('event')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'event'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <PartyPopper className={`w-8 h-8 ${selectedLocation === 'event' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Event</span>
              </button>
              <button
                onClick={() => setSelectedLocation('field')}
                className={`p-6 border-2 rounded-xl flex flex-col items-center gap-3 transition-all ${
                  selectedLocation === 'field'
                    ? 'border-primary bg-primary/5'
                    : 'border-slate-200 hover:border-primary/50'
                }`}
              >
                <Briefcase className={`w-8 h-8 ${selectedLocation === 'field' ? 'text-primary' : 'text-slate-400'}`} />
                <span className="font-semibold">Field</span>
              </button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLocationDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => handleClockIn(selectedLocation)}
                disabled={!selectedLocation}
              >
                Clock In
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}

export default TimeTracking;